import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { adminApi } from '../../api/admin/adminApi';
import { formatVerificationRate } from './analytics/analyticsFormat';
import { registerAdminCharts } from './analytics/registerAdminCharts';
import styles from './UsersTab.module.css';

registerAdminCharts();

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

function cardStatusLabel(status) {
  if (status === 'active') return 'Active';
  if (status === 'revoked') return 'Revoked';
  return status || '';
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function UsersTab({ analyticsUsers = null, recentRegistrations = null, showRecentRegistrations = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const userFromUrl = searchParams.get('user');

  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q, 300);

  const [sortBy, setSortBy] = useState('signupDate');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedUserId, setSelectedUserId] = useState(userFromUrl);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detail, setDetail] = useState(null);
  const cardsRef = useRef(null);
  const connectionsRef = useRef(null);

  const pages = Math.max(1, Math.ceil(totalCount / limit));

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await adminApi.listUsers({
        q: debouncedQ || undefined,
        page,
        limit,
        sortBy,
        sortDir
      });
      setRows(result.rows || []);
      setTotalCount(result.totalCount || 0);
    } catch (e) {
      setError('Failed to load users');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, limit, page, sortBy, sortDir]);

  const fetchDetail = useCallback(async (id) => {
    try {
      setDetailLoading(true);
      setDetailError('');
      const result = await adminApi.getUserDetail(id);
      setDetail(result.data || null);
    } catch (e) {
      setDetailError('Failed to load user detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (userFromUrl) setSelectedUserId(userFromUrl);
  }, [userFromUrl]);

  useEffect(() => {
    if (!selectedUserId) return;
    fetchDetail(selectedUserId);
  }, [fetchDetail, selectedUserId]);

  const openUser = (id) => {
    if (!id) return;
    setSelectedUserId(id);
    const next = new URLSearchParams(searchParams);
    next.set('user', id);
    setSearchParams(next, { replace: true });
  };

  const closeDrawer = () => {
    setSelectedUserId(null);
    setDetail(null);
    if (searchParams.has('user')) {
      const next = new URLSearchParams(searchParams);
      next.delete('user');
      setSearchParams(next, { replace: true });
    }
  };

  // Reset paging when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, sortBy, sortDir]);

  const onSort = (nextBy) => {
    if (nextBy === sortBy) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(nextBy);
      setSortDir('asc');
    }
  };

  const chartData = useMemo(() => {
    const series = detail?.sessionsSeries30d || [];
    return {
      labels: series.map((s) => s.date),
      datasets: [
        {
          label: 'Sessions',
          data: series.map((s) => s.sessions || 0),
          borderColor: '#334155',
          backgroundColor: 'rgba(51, 65, 85, 0.08)',
          tension: 0.2,
          fill: true
        }
      ]
    };
  }, [detail?.sessionsSeries30d]);

  return (
    <div className="analytics-section">
      <div className="admin-table-header">
        <div>
          <h2>Users</h2>
          <p className="admin-table-subtitle">Search, sort, and open per-user analytics (last 30 days).</p>
        </div>
        <div className="admin-table-actions">
          <button className="btn" disabled={loading} onClick={() => fetchUsers()}>
            Refresh
          </button>
        </div>
      </div>

      <div className="admin-table-filters">
        <input
          type="text"
          placeholder="Search name/email/company…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {analyticsUsers ? (
        <>
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-value">{analyticsUsers.total || 0}</span>
              <span className="metric-label">Total Users</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{analyticsUsers.newToday || 0}</span>
              <span className="metric-label">New Today</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{analyticsUsers.activeToday || 0}</span>
              <span className="metric-label">Active Today</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{formatVerificationRate(analyticsUsers.verificationRate)}</span>
              <span className="metric-label">Verification Rate</span>
            </div>
          </div>

          {showRecentRegistrations && Array.isArray(recentRegistrations) && recentRegistrations.length ? (
            <div className="activity-card">
              <h3>Recent Registrations</h3>
              {recentRegistrations.slice(0, 10).map((u, idx) => (
                <div key={idx} className="activity-item">
                  <span>{u.email} {u.isVerified ? '✅' : '⏳'}</span>
                  <span>{u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {error ? (
        <div className="error-message">
          {error} <button className="btn" onClick={() => fetchUsers()} style={{ marginLeft: 8 }}>Retry</button>
        </div>
      ) : null}
      {loading ? <div className={styles.skeleton}>Loading users…</div> : null}

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th className={styles.sortable} onClick={() => onSort('name')}>Name</th>
              <th className={styles.sortable} onClick={() => onSort('email')}>Email</th>
              <th className={styles.sortable} onClick={() => onSort('company')}>Company</th>
              <th className={styles.sortable} onClick={() => onSort('role')}>Role/Type</th>
              <th className={styles.sortable} onClick={() => onSort('signupDate')}>Signup Date</th>
              <th className={styles.sortable} onClick={() => onSort('lastLoginAt')}>Last Login</th>
              <th className={styles.sortable} onClick={() => onSort('sessions30d')}>Sessions (30d)</th>
              <th className={styles.sortable} onClick={() => onSort('avgSessionDuration30dSeconds')}>Avg Duration (30d)</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="adminMutedText" style={{ textAlign: 'center', padding: '1.5rem' }}>
                  No users found
                </td>
              </tr>
            ) : null}
            {rows.map((u) => (
              <tr
                key={u._id}
                className="admin-row-clickable"
                onClick={() => openUser(u._id)}
              >
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.company || ''}</td>
                <td>{u.role || ''}</td>
                <td>{formatDate(u.signupDate)}</td>
                <td>{formatDateTime(u.lastLoginAt)}</td>
                <td>{u.sessions30d ?? 0}</td>
                <td>{u.avgSessionDuration30dSeconds ?? 0}s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-table-pagination">
        <button className="btn" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
        <span className="adminMutedText">
          Page {page} / {pages} (Total {totalCount})
        </span>
        <button className="btn" disabled={loading || page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next</button>
      </div>

      {selectedUserId ? (
        <div className="admin-drawer-overlay" onClick={closeDrawer}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h3>User Detail</h3>
              <button className="admin-drawer-close" onClick={closeDrawer}>×</button>
            </div>

            <div className="admin-drawer-body">
              {detailLoading ? <div className={styles.skeleton}>Loading user…</div> : null}
              {detailError ? <div className="error-message">{detailError}</div> : null}

              {detail?.user ? (
                <>
                  <div className="admin-field">
                    <div className="admin-field-label">User</div>
                    <div className="admin-field-value">{detail.user.name} — {detail.user.email}</div>
                  </div>
                  <div className="admin-field">
                    <div className="admin-field-label">Company</div>
                    <div className="admin-field-value">{detail.user.company || ''}</div>
                  </div>

                  <div className="admin-field">
                    <div className="admin-field-label">Timeline</div>
                    <div className="admin-field-pre">
                      Signup: {formatDateTime(detail.timeline?.signupDate)}{'\n'}
                      First login: {formatDateTime(detail.timeline?.firstLoginAt)}{'\n'}
                      Last login: {formatDateTime(detail.timeline?.lastLoginAt)}
                    </div>
                  </div>

                  <div className="admin-field">
                    <div className="admin-field-label">Sessions (last 30 days)</div>
                    <div className={styles.chartWrap}>
                      <Line
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { y: { beginAtZero: true } }
                        }}
                      />
                    </div>
                    {(!detail.sessionsSeries30d || detail.sessionsSeries30d.every((d) => (d.sessions || 0) === 0)) ? (
                      <div className="adminMutedText" style={{ fontSize: 13, marginTop: 8 }}>No sessions in the last 30 days</div>
                    ) : null}
                  </div>

                  <div className={styles.actionsRow}>
                    <button type="button" className="btn" onClick={() => cardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      View cards ({detail.user.cardsCreated ?? detail.cards?.length ?? 0})
                    </button>
                    <button type="button" className="btn" onClick={() => connectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      View connections ({detail.user.connectionsCreated ?? detail.connections?.length ?? 0})
                    </button>
                  </div>

                  <div ref={cardsRef} className="admin-field">
                    <div className="admin-field-label">Cards</div>
                    {detail.publicCardUrl ? (
                      <a className={styles.drawerLink} href={detail.publicCardUrl} target="_blank" rel="noreferrer">
                        Public profile
                      </a>
                    ) : (
                      <div className="adminMutedText" style={{ fontSize: 13 }}>No public profile on this account.</div>
                    )}
                    {(detail.cards || []).length ? (
                      <ul className={styles.drawerList}>
                        {detail.cards.map((card) => (
                          <li key={card.cardId}>
                            <a href={`/nfc/${encodeURIComponent(card.cardId)}`} target="_blank" rel="noreferrer">
                              {card.cardId}
                            </a>
                            <span className="adminMutedText">
                              {cardStatusLabel(card.status)}
                              {card.assignedAt ? ` · ${formatDate(card.assignedAt)}` : ''}
                              {card.source ? ` · ${card.source}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="adminMutedText" style={{ fontSize: 13, marginTop: 6 }}>No physical cards on this account.</div>
                    )}
                  </div>

                  <div ref={connectionsRef} className="admin-field">
                    <div className="admin-field-label">Connections</div>
                    {(detail.connections || []).length ? (
                      <ul className={styles.drawerList}>
                        {detail.connections.map((contact) => (
                          <li key={contact._id}>
                            <span>{contact.name || contact.email}</span>
                            <span className="adminMutedText">
                              {[contact.company, contact.email].filter(Boolean).join(' · ')}
                              {contact.dateAdded ? ` · ${formatDate(contact.dateAdded)}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="adminMutedText" style={{ fontSize: 13 }}>No saved connections yet.</div>
                    )}
                  </div>
                </>
              ) : null}
            </div>

            <div className="admin-drawer-footer">
              <button className="btn" onClick={closeDrawer}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

